import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Leaf, Flame, Zap, Apple, Salad } from 'lucide-react';

const MESSAGES = [
  { title: 'Анализираме профила ти...', sub: 'TDEE, BMR и макро цели' },
  { title: 'Избираме оптималните храни...', sub: 'Адаптирано за България' },
  { title: 'Балансираме макронутриентите...', sub: 'Белтъци, въглехидрати, мазнини' },
  { title: 'Съставяме рецептите...', sub: '28 ястия за 7 дни' },
  { title: 'Финализираме плана ти...', sub: 'Почти готово!' },
];

const PARTICLES = [
  { icon: Leaf,    x: '8%',  dur: 7,  delay: 0,   size: 20, op: 0.25 },
  { icon: ChefHat, x: '88%', dur: 9,  delay: 1.2, size: 22, op: 0.2  },
  { icon: Flame,   x: '25%', dur: 8,  delay: 0.4, size: 18, op: 0.2  },
  { icon: Zap,     x: '72%', dur: 7,  delay: 2.1, size: 20, op: 0.18 },
  { icon: Leaf,    x: '50%', dur: 10, delay: 0.8, size: 16, op: 0.15 },
  { icon: Flame,   x: '15%', dur: 8,  delay: 3.0, size: 22, op: 0.15 },
  { icon: ChefHat, x: '62%', dur: 9,  delay: 1.7, size: 18, op: 0.2  },
];

export function CinematicLoader({ progress = 0 }) {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const idx =
      progress < 20 ? 0
      : progress < 40 ? 1
      : progress < 60 ? 2
      : progress < 82 ? 3
      : 4;
    setMsgIndex(Math.min(idx, MESSAGES.length - 1));
  }, [progress]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden select-none"
      style={{ background: 'linear-gradient(145deg, #020c07 0%, #031509 60%, #041208 100%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="cinematic-loader"
    >
      {/* Ambient radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 55%, rgba(16,185,129,0.12) 0%, transparent 70%)',
        }}
      />

      {/* Floating particles */}
      {PARTICLES.map((p, i) => {
        const Icon = p.icon;
        return (
          <motion.div
            key={i}
            className="absolute bottom-0 pointer-events-none text-emerald-400"
            style={{ left: p.x, opacity: 0 }}
            animate={{
              y: [0, '-105vh'],
              opacity: [0, p.op, p.op, 0],
            }}
            transition={{
              duration: p.dur,
              delay: p.delay,
              repeat: Infinity,
              ease: 'linear',
              times: [0, 0.1, 0.85, 1],
            }}
          >
            <Icon style={{ width: p.size, height: p.size }} strokeWidth={1.5} />
          </motion.div>
        );
      })}

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center text-center px-8">
        {/* Pulsing icon */}
        <div className="relative mb-10 flex items-center justify-center">
          {/* Outer pulse rings */}
          {[0, 1].map(n => (
            <motion.div
              key={n}
              className="absolute rounded-full border border-emerald-400/30"
              style={{ width: 80 + n * 40, height: 80 + n * 40 }}
              animate={{ scale: [1, 1.4, 1.4], opacity: [0.5, 0, 0] }}
              transition={{
                duration: 2,
                delay: n * 0.5,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}
          {/* Icon circle */}
          <div
            className="relative w-20 h-20 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
              boxShadow: '0 0 40px rgba(52,211,153,0.45), 0 0 80px rgba(52,211,153,0.15)',
            }}
          >
            <ChefHat className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>
        </div>

        {/* Animated message */}
        <AnimatePresence mode="wait">
          <motion.div
            key={msgIndex}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-2"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
              {MESSAGES[msgIndex].title}
            </h2>
            <p className="text-emerald-400/70 text-sm">{MESSAGES[msgIndex].sub}</p>
          </motion.div>
        </AnimatePresence>

        {/* Progress percentage */}
        <motion.p
          className="text-5xl font-black text-emerald-400 tabular-nums mt-10 tracking-tight"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          {progress}%
        </motion.p>

        {/* Sub-label */}
        <p className="text-slate-500 text-sm mt-2">Моля, изчакайте…</p>
      </div>

      {/* Bottom progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800/60">
        <motion.div
          className="h-full rounded-r-full"
          style={{ background: 'linear-gradient(90deg, #34d399, #10b981, #059669)' }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </motion.div>
  );
}
